// frontend/app/components/lessonPlans/present/PresentCheckBlocks.js
//
// The check blocks, re-presented as questions to a room rather than as
// questions to a student.
//
// Every interactive affordance is deliberately gone. There is no TextInput, no
// TouchableOpacity around an option, no accessibilityRole of radio or checkbox
// and no selected state — because nothing here can be selected. Thirty students
// with no devices answer out loud, and the single teacher-controlled Reveal is
// the only state this surface has. That is not a simplification of the
// workspace's version; it is the whole interaction model, and it is why these
// components take no onAnswer and no onCheck to accidentally call.

import React from "react";
import { StyleSheet, Text, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import {
  PresentBody,
  PresentEyebrow,
  PresentPrompt,
  PresentRevealPanel,
} from "./PresentSurface";

const optionLetter = (index) => String.fromCharCode(65 + index);

/**
 * One option, at a size that carries to the back row.
 *
 * After the reveal the correct options gain a tick and a green field, and the
 * rest are dimmed rather than removed — the room needs to see which of the
 * things they argued about was the one that was right.
 */
const PresentOption = ({ type, colors, index, text, revealed, isCorrect, keyed }) => {
  const marker = Math.round(type.option.fontSize * 1.75);
  const dimmed = revealed && keyed && !isCorrect;

  return (
    <View
      style={[
        styles.option,
        {
          backgroundColor:
            revealed && isCorrect ? colors.correctBg : colors.subtleBg,
          borderColor:
            revealed && isCorrect ? colors.correctBorder : colors.border,
          borderWidth: revealed && isCorrect ? 2 : 1,
          opacity: dimmed ? 0.45 : 1,
          paddingVertical: Math.round(type.option.fontSize * 0.6),
          paddingHorizontal: Math.round(type.option.fontSize * 0.7),
        },
      ]}
    >
      <View
        style={[
          styles.optionMarker,
          {
            width: marker,
            height: marker,
            borderRadius: Math.round(marker / 2),
            backgroundColor:
              revealed && isCorrect ? colors.success : "transparent",
            borderColor:
              revealed && isCorrect ? colors.success : colors.borderStrong,
          },
        ]}
      >
        {revealed && isCorrect ? (
          <MaterialCommunityIcons
            name="check"
            size={Math.round(marker * 0.62)}
            color="#ffffff"
          />
        ) : (
          <Text
            style={[
              styles.optionLetter,
              {
                color: colors.textMuted,
                fontSize: Math.round(type.option.fontSize * 0.8),
              },
            ]}
          >
            {optionLetter(index)}
          </Text>
        )}
      </View>

      <Text style={[styles.optionText, type.option, { color: colors.textPrimary }]}>
        {text}
      </Text>
    </View>
  );
};

/** `multiple_choice` and `multi_select` — the same slide, one extra instruction. */
export const PresentChoiceBlock = ({
  block,
  colors,
  type,
  reveal,
  revealed,
  t,
}) => {
  const translate = t || ((value) => value);
  const options = Array.isArray(block.options) ? block.options : [];
  const correctIds = reveal ? reveal.correctOptionIds : [];
  const keyed = correctIds.length > 0;

  return (
    <View>
      <PresentPrompt type={type} colors={colors}>
        {block.prompt}
      </PresentPrompt>

      {block.type === "multi_select" ? (
        <Text style={[styles.instruction, type.caption, { color: colors.textMuted }]}>
          {translate("More than one answer is correct.")}
        </Text>
      ) : null}

      <View style={styles.optionList}>
        {options.map((option, index) => (
          <PresentOption
            key={`${block.id}-${option.id}`}
            type={type}
            colors={colors}
            index={index}
            text={option.text}
            revealed={revealed}
            keyed={keyed}
            isCorrect={correctIds.includes(option.id)}
          />
        ))}
      </View>

      {revealed && reveal?.explanation ? (
        <PresentRevealPanel
          type={type}
          colors={colors}
          label={translate("Why")}
        >
          <PresentBody type={type} colors={colors} color={colors.textPrimary}>
            {reveal.explanation}
          </PresentBody>
        </PresentRevealPanel>
      ) : null}
    </View>
  );
};

/**
 * `ordering`. Before the reveal the items are a pool — the room argues about
 * the sequence; after it, the same items are a numbered answer. Showing the
 * pool unnumbered first matters: a numbered list on screen would answer the
 * question before it was asked.
 */
export const PresentSequenceBlock = ({
  block,
  colors,
  type,
  reveal,
  revealed,
  t,
}) => {
  const translate = t || ((value) => value);
  const items = Array.isArray(block.items) ? block.items : [];
  const sequence = reveal ? reveal.sequence : [];
  const showSequence = revealed && sequence.length > 0;

  return (
    <View>
      <PresentPrompt type={type} colors={colors}>
        {block.prompt}
      </PresentPrompt>

      <Text style={[styles.instruction, type.caption, { color: colors.textMuted }]}>
        {showSequence
          ? translate("The correct order:")
          : translate("Put these in order.")}
      </Text>

      <View style={showSequence ? styles.optionList : styles.pool}>
        {showSequence
          ? sequence.map((item, index) => (
              <View
                key={`${block.id}-order-${item.id}`}
                style={[
                  styles.option,
                  {
                    backgroundColor: colors.correctBg,
                    borderColor: colors.correctBorder,
                    borderWidth: 2,
                    paddingVertical: Math.round(type.option.fontSize * 0.6),
                    paddingHorizontal: Math.round(type.option.fontSize * 0.7),
                  },
                ]}
              >
                <View
                  style={[
                    styles.optionMarker,
                    {
                      width: Math.round(type.option.fontSize * 1.75),
                      height: Math.round(type.option.fontSize * 1.75),
                      borderRadius: Math.round(type.option.fontSize * 0.88),
                      backgroundColor: colors.success,
                      borderColor: colors.success,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.optionLetter,
                      {
                        color: "#ffffff",
                        fontSize: Math.round(type.option.fontSize * 0.8),
                      },
                    ]}
                  >
                    {index + 1}
                  </Text>
                </View>
                <Text
                  style={[styles.optionText, type.option, { color: colors.textPrimary }]}
                >
                  {item.text}
                </Text>
              </View>
            ))
          : items.map((item) => (
              <View
                key={`${block.id}-pool-${item.id}`}
                style={[
                  styles.poolChip,
                  {
                    backgroundColor: colors.subtleBg,
                    borderColor: colors.border,
                    paddingVertical: Math.round(type.option.fontSize * 0.5),
                    paddingHorizontal: Math.round(type.option.fontSize * 0.8),
                  },
                ]}
              >
                <Text
                  style={[styles.optionText, type.option, { color: colors.textPrimary }]}
                >
                  {item.text}
                </Text>
              </View>
            ))}
      </View>

      {revealed && reveal?.explanation ? (
        <PresentRevealPanel type={type} colors={colors} label={translate("Why")}>
          <PresentBody type={type} colors={colors} color={colors.textPrimary}>
            {reveal.explanation}
          </PresentBody>
        </PresentRevealPanel>
      ) : null}
    </View>
  );
};

/**
 * Every free-text block: short_answer, prediction, explanation,
 * exploration_probe, reflection and next_challenge.
 *
 * With no devices in the room these are all the same thing — a question the
 * teacher puts to the class — so they share one renderer and differ only in the
 * kicker above the prompt and in whether they have authored guidance to reveal.
 * There is no input field on any of them, which is the point.
 */
export const PresentDiscussBlock = ({
  block,
  colors,
  type,
  reveal,
  revealed,
  accent,
  eyebrow,
  t,
}) => {
  const translate = t || ((value) => value);
  const prompts = Array.isArray(block.prompts) ? block.prompts : [];
  // A reflection authors several prompts and no single `prompt`. The confusion
  // field is a student self-report in the workspace; asked out loud it is one
  // more good question, so it joins the list unless the author removed it.
  const confusion =
    block.type === "reflection" && block.confusion_prompt !== null
      ? block.confusion_prompt || translate("Anything still confusing?")
      : null;

  // A reflection carries a title and a list of prompts but no `prompt` of its
  // own, so the headline has to fall back rather than leaving the slide
  // opening on a bare bullet list.
  const headline =
    block.prompt ||
    block.title ||
    (block.type === "reflection" ? translate("Look back") : null);

  return (
    <View>
      {block.next_loop_hint ? (
        <PresentEyebrow type={type} color={accent || colors.accentPrimary}>
          {block.next_loop_hint}
        </PresentEyebrow>
      ) : eyebrow ? (
        <PresentEyebrow type={type} color={accent || colors.accentPrimary}>
          {translate(eyebrow)}
        </PresentEyebrow>
      ) : null}

      <PresentPrompt type={type} colors={colors}>
        {headline}
      </PresentPrompt>

      {/* The confusion question stands on its own: a reflection authored with
          nothing but that field would otherwise lose its only question. */}
      {prompts.length > 0 || confusion ? (
        <View style={styles.promptList}>
          {prompts
            .filter((entry) => entry?.label)
            .map((entry) => (
              <View key={`${block.id}-${entry.key}`} style={styles.promptRow}>
                <View
                  style={[styles.promptDot, { backgroundColor: accent || colors.accentPrimary }]}
                />
                <Text
                  style={[styles.promptText, type.option, { color: colors.textPrimary }]}
                >
                  {entry.label}
                </Text>
              </View>
            ))}
          {confusion ? (
            <View style={styles.promptRow}>
              <View
                style={[styles.promptDot, { backgroundColor: colors.warning }]}
              />
              <Text
                style={[styles.promptText, type.option, { color: colors.textPrimary }]}
              >
                {confusion}
              </Text>
            </View>
          ) : null}
        </View>
      ) : null}

      {revealed && reveal ? (
        <PresentRevealPanel
          type={type}
          colors={colors}
          label={translate(reveal.label || "Look for")}
        >
          {reveal.notes.map((note, index) => (
            <View key={`${block.id}-note-${index}`} style={styles.noteRow}>
              <View style={[styles.noteDot, { backgroundColor: colors.success }]} />
              <PresentBody
                type={type}
                colors={colors}
                color={colors.textPrimary}
                style={styles.noteText}
              >
                {note}
              </PresentBody>
            </View>
          ))}
        </PresentRevealPanel>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  instruction: { fontStyle: "italic", marginTop: 14 },

  optionList: { marginTop: 22, gap: 14 },
  option: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 14,
  },
  optionMarker: {
    borderWidth: 2,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 18,
  },
  optionLetter: { fontWeight: "800" },
  optionText: { flex: 1, fontWeight: "500" },

  pool: { marginTop: 22, flexDirection: "row", flexWrap: "wrap", gap: 12 },
  poolChip: { borderRadius: 999, borderWidth: 1 },

  promptList: { marginTop: 24, gap: 16 },
  promptRow: { flexDirection: "row", alignItems: "flex-start" },
  promptDot: { width: 10, height: 10, borderRadius: 5, marginTop: 12, marginRight: 16 },
  promptText: { flex: 1, fontWeight: "500" },

  noteRow: { flexDirection: "row", alignItems: "flex-start", marginBottom: 10 },
  noteDot: { width: 8, height: 8, borderRadius: 4, marginTop: 12, marginRight: 14 },
  noteText: { flex: 1 },
});
